import * as React from "react";
import { Alert, Button, Card, Divider, Dropdown, notification } from "antd";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { CardContent } from "../../../components/antd/card/CardContent";
import { useDrawerContext } from "../../../components/antd/drawerContext";
import { getMetricOptions } from "./utils/utils";
import { KedaTrigger, WorkloadAutoscalingKeda } from "../../../schema/types/workload/workloadOptions";
import jsYaml from "js-yaml";
import { allKedaScalers, kedaScalerOptions } from "./kedaUtils";
import { ExternalLink } from "../../../newcomponents/table/components/ExternalLink";
import { CodeEditor } from "../../group/identityMatcher/codeEditor";
import { DOCS_URL } from "../../../envVariables";
import { DrawerWorkloadOptions } from "../../../components/drawer/options";
import { formSet, formSetValue, useCpFormValue } from "../../gvc/testNewForm/utils";
import { CpFormItem } from "../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../gvc/testNewForm/CpLabel";
import { CpDescription } from "../../gvc/testNewForm/CpDescription";
import { CpErrorWarning } from "../../gvc/testNewForm/CpErrorWarning";
import { CpSwitch } from "../../gvc/testNewForm/CpSwitch";
import { CpSelect } from "../../gvc/testNewForm/CpSelect";
import { CpInput } from "../../gvc/testNewForm/CpInput";
import { CpInputNumber } from "../../gvc/testNewForm/CpInputNumber";
import { CpErrorWarningArray } from "../../gvc/testNewForm/CpErrorWarningArray";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { CpLabelTooltip } from "../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../gvc/testNewForm/CpTooltip";
import { CpLabelDirtyIndicator } from "../../gvc/testNewForm/CpLabelDirtyIndicator";
import { Only } from "./only";
import { store } from "../../../store";
import { GvcLocationOption } from "./GvcLocationFetcher";

interface Props {
  index: number;
}
export const WorkloadLocalOptions: React.FC<Props> = React.memo(({ index }) => {
  const { id } = useCpFormContext();
  const drawer = useDrawerContext();

  const type = useCpFormValue(`type`);
  const gvc = useCpFormValue(`gvc`);
  const gvcLocations: GvcLocationOption[] = useCpFormValue(`meta.gvcLocations`) || [];
  const gvcLocationsFetchFailed: boolean = useCpFormValue(`meta.gvcLocationsFetchFailed`) || false;
  const allLocalOptions: Array<{ location: string }> = useCpFormValue(`localOptions`) || [];

  const availableLocations = React.useMemo(() => {
    const usedByOthers = new Set(
      allLocalOptions
        .filter((_, i) => i !== index)
        .map((lo) => lo.location)
        .filter(Boolean),
    );
    return gvcLocations.filter((loc) => !usedByOthers.has(loc.value));
  }, [gvcLocations, allLocalOptions, index]);

  const capacityAI = useCpFormValue(`localOptions.${index}.capacityAI`);
  const metric = useCpFormValue(`localOptions.${index}.autoscaling.metric`);
  const keda = useCpFormValue(`localOptions.${index}.autoscaling.keda`);
  const minScale = useCpFormValue(`localOptions.${index}.autoscaling.minScale`);

  const [scaleToZeroSuggestionApplied, setScaleToZeroSuggestionApplied] = React.useState(false);

  React.useEffect(() => {
    if (minScale !== "0") {
      setScaleToZeroSuggestionApplied(false);
    }
  }, [minScale]);

  const applyScaleToZeroSuggestion = React.useCallback(() => {
    formSetValue(id, `localOptions.${index}.autoscaling.scaleToZeroDelay`, "30");
    formSetValue(id, `localOptions.${index}.timeoutSeconds`, "120");
    formSetValue(id, `localOptions.${index}.autoscaling.metric`, "concurrency");
    setScaleToZeroSuggestionApplied(true);
  }, [id, index]);

  const metricOptions = React.useMemo(() => {
    return getMetricOptions(type);
  }, [type]);

  return (
    <div className="cpln-form-section cpln-ant-tab-content">
      <Card>
        <CardTitle>Local Options</CardTitle>
        <CardContent>
          <CpFormItem noMargin>
            <CpLabel name={`localOptions.${index}.location`}>Location</CpLabel>
            {gvcLocationsFetchFailed && gvcLocations.length === 0 ? (
              <CpInput
                name={`localOptions.${index}.location`}
                placeholder="Enter a location"
                style={{ width: 600 }}
              />
            ) : (
              <CpSelect
                name={`localOptions.${index}.location`}
                placeholder="Select a location"
                options={availableLocations}
                style={{ width: 600 }}
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
              />
            )}
            <CpErrorWarning name={`localOptions.${index}.location`} />
          </CpFormItem>
          {type == "cron" ? (
            <>
              <CpFormItem>
                <div
                  className="text-sm link cursor-pointer"
                  onClick={() =>
                    drawer.setTemporary(
                      DrawerWorkloadOptions.key,
                      DrawerWorkloadOptions.title,
                      DrawerWorkloadOptions.Content(type),
                    )
                  }
                >
                  Details
                </div>
                <div className="flex items-center gap-16">
                  {type === "stateful" ? null : (
                    <div className="flex items-center gap-2">
                      <CpLabel name={`localOptions.${index}.capacityAI`}>
                        Capacity AI
                        <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                          <CpLabelDirtyIndicator name={`localOptions.${index}.capacityAI`} />
                        </Only>
                      </CpLabel>
                      <CpSwitch name={`localOptions.${index}.capacityAI`} disabled={metric === "cpu"} />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <CpLabel name={`localOptions.${index}.suspend`}>
                      Suspend
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`localOptions.${index}.suspend`} />
                      </Only>
                    </CpLabel>
                    <CpSwitch name={`localOptions.${index}.suspend`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <CpLabel name={`localOptions.${index}.multiZone.isEnabled`}>
                      Multi Zone
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`localOptions.${index}.multiZone.isEnabled`} />
                      </Only>
                    </CpLabel>
                    <CpSwitch name={`localOptions.${index}.multiZone.isEnabled`} />
                  </div>
                </div>
                <CpErrorWarningArray
                  names={[
                    `localOptions.${index}.capacityAI`,
                    `localOptions.${index}.suspend`,
                    `localOptions.${index}.multiZone.isEnabled`,
                  ]}
                />
              </CpFormItem>
              {capacityAI ? (
                <CpFormItem>
                  <CpLabelTooltip>
                    <CpLabel name={`localOptions.${index}.capacityAIUpdateMinutes`}>
                      Capacity AI Update Minutes
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`localOptions.${index}.capacityAIUpdateMinutes`} />
                      </Only>
                    </CpLabel>
                    <CpTooltip tooltipProps={{ title: `How often reservations are adjusted` }} />
                  </CpLabelTooltip>
                  <CpInputNumber name={`localOptions.${index}.capacityAIUpdateMinutes`} style={{ width: 140 }} />
                  <CpErrorWarning name={`localOptions.${index}.capacityAIUpdateMinutes`} />
                </CpFormItem>
              ) : null}
            </>
          ) : null}
          {type !== "cron" ? (
            <>
              <CpFormItem>
                {metric === "cpu" ? (
                  <CpDescription>
                    Capacity AI is not available when Scaling Strategy is set to CPU Utilization
                  </CpDescription>
                ) : null}
                <div
                  className="text-sm link cursor-pointer"
                  onClick={() =>
                    drawer.setTemporary(
                      DrawerWorkloadOptions.key,
                      DrawerWorkloadOptions.title,
                      DrawerWorkloadOptions.Content(type),
                    )
                  }
                >
                  Details
                </div>
                <div className="flex items-center gap-16">
                  <div className="flex items-center gap-2">
                    <CpLabel name={`localOptions.${index}.capacityAI`}>
                      Capacity AI
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`localOptions.${index}.capacityAI`} />
                      </Only>
                    </CpLabel>
                    <CpSwitch name={`localOptions.${index}.capacityAI`} disabled={metric === "cpu"} />
                  </div>
                  <div className="flex items-center gap-2">
                    <CpLabel name={`localOptions.${index}.debug`}>
                      Debug
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`localOptions.${index}.debug`} />
                      </Only>
                    </CpLabel>
                    <CpSwitch name={`localOptions.${index}.debug`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <CpLabel name={`localOptions.${index}.suspend`}>
                      Suspend
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`localOptions.${index}.suspend`} />
                      </Only>
                    </CpLabel>
                    <CpSwitch name={`localOptions.${index}.suspend`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <CpLabel name={`localOptions.${index}.multiZone.isEnabled`}>
                      Multi Zone
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`localOptions.${index}.multiZone.isEnabled`} />
                      </Only>
                    </CpLabel>
                    <CpSwitch name={`localOptions.${index}.multiZone.isEnabled`} />
                  </div>
                </div>
                <CpErrorWarningArray
                  names={[
                    `localOptions.${index}.capacityAI`,
                    `localOptions.${index}.debug`,
                    `localOptions.${index}.suspend`,
                    `localOptions.${index}.multiZone.isEnabled`,
                  ]}
                />
              </CpFormItem>
              {capacityAI ? (
                <CpFormItem>
                  <CpLabelTooltip>
                    <CpLabel name={`localOptions.${index}.capacityAIUpdateMinutes`}>
                      Capacity AI Update Minutes
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`localOptions.${index}.capacityAIUpdateMinutes`} />
                      </Only>
                    </CpLabel>
                    <CpTooltip tooltipProps={{ title: `How often reservations are adjusted` }} />
                  </CpLabelTooltip>
                  <CpInputNumber name={`localOptions.${index}.capacityAIUpdateMinutes`} />
                  <CpErrorWarning name={`localOptions.${index}.capacityAIUpdateMinutes`} />
                </CpFormItem>
              ) : null}
              <CpFormItem>
                <CpLabel name={`localOptions.${index}.timeoutSeconds`}>
                  Timeout Seconds
                  <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                    <CpLabelDirtyIndicator name={`localOptions.${index}.timeoutSeconds`} />
                  </Only>
                </CpLabel>
                <CpInputNumber name={`localOptions.${index}.timeoutSeconds`} />
                <CpErrorWarning name={`localOptions.${index}.timeoutSeconds`} />
              </CpFormItem>
              <Divider orientation={`left`} orientationMargin={0}>
                Auto Scaling
              </Divider>
              {metric === "keda" ? (
                <div>
                  {metric === "keda" && !gvc.item?.spec.keda?.enabled ? (
                    <Alert
                      showIcon
                      className="my-2 w-fit"
                      type={`warning`}
                      message={
                        <div>
                          <div>
                            KEDA autoscaling is configured for this workload but will not function because KEDA is not
                            enabled in its GVC.
                          </div>
                          <div>Please enable KEDA in the GVC settings to activate KEDA autoscaling.</div>
                        </div>
                      }
                    />
                  ) : null}
                </div>
              ) : null}
              <CpFormItem>
                <div className="flex items-start gap-4">
                  <div className="flex flex-col">
                    <CpLabel name={`localOptions.${index}.autoscaling.metric`}>
                      Metric
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`localOptions.${index}.autoscaling.metric`} />
                      </Only>
                    </CpLabel>
                    <CpSelect
                      name={`localOptions.${index}.autoscaling.metric`}
                      options={metricOptions}
                      style={{ minWidth: 300 }}
                    />
                  </div>
                  {["disabled", "multi", "keda"].includes(metric) ? null : (
                    <div className="flex flex-col">
                      <CpLabel name={`localOptions.${index}.autoscaling.target`}>
                        Target
                        <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                          <CpLabelDirtyIndicator name={`localOptions.${index}.autoscaling.target`} />
                        </Only>
                      </CpLabel>
                      <CpInputNumber name={`localOptions.${index}.autoscaling.target`} />
                    </div>
                  )}
                  {metric === "latency" ? (
                    <div className="flex flex-col">
                      <CpLabel name={`localOptions.${index}.autoscaling.metricPercentile`}>
                        Metric Percentile
                        <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                          <CpLabelDirtyIndicator name={`localOptions.${index}.autoscaling.metricPercentile`} />
                        </Only>
                      </CpLabel>
                      <CpSelect
                        name={`localOptions.${index}.autoscaling.metricPercentile`}
                        options={[
                          { label: "p50", value: "p50" },
                          { label: "p75", value: "p75" },
                          { label: "p99", value: "p99" },
                        ]}
                      />
                    </div>
                  ) : null}
                  {metric === "multi" ? (
                    <>
                      <div className="flex flex-col">
                        <CpLabel name={`localOptions.${index}.autoscaling.multi.cpu`}>
                          CPU Target
                          <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                            <CpLabelDirtyIndicator name={`localOptions.${index}.autoscaling.multi.cpu`} />
                          </Only>
                        </CpLabel>
                        <CpInputNumber name={`localOptions.${index}.autoscaling.multi.cpu`} />
                      </div>
                      <div className="flex flex-col">
                        <CpLabel name={`localOptions.${index}.autoscaling.multi.memory`}>
                          Memory Target
                          <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                            <CpLabelDirtyIndicator name={`localOptions.${index}.autoscaling.multi.memory`} />
                          </Only>
                        </CpLabel>
                        <CpInputNumber name={`localOptions.${index}.autoscaling.multi.memory`} />
                      </div>
                      <div className="flex flex-col">
                        <CpLabel name={`localOptions.${index}.autoscaling.multi.rps`}>
                          RPS Target
                          <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                            <CpLabelDirtyIndicator name={`localOptions.${index}.autoscaling.multi.rps`} />
                          </Only>
                        </CpLabel>
                        <CpInputNumber name={`localOptions.${index}.autoscaling.multi.rps`} />
                      </div>
                    </>
                  ) : null}
                </div>
                <CpErrorWarningArray
                  names={[
                    `localOptions.${index}.autoscaling.metric`,
                    `localOptions.${index}.autoscaling.target`,
                    `localOptions.${index}.autoscaling.metricPercentile`,
                    `localOptions.${index}.autoscaling.multi.cpu`,
                    `localOptions.${index}.autoscaling.multi.memory`,
                    `localOptions.${index}.autoscaling.multi.rps`,
                  ]}
                />
              </CpFormItem>
              {metric === "keda" ? (
                <div className="mb-8">
                  <div className="flex items-center gap-4">
                    <div className="font-semibold w-fit">
                      KEDA Configuration
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`localOptions.${index}.autoscaling.keda`} />
                      </Only>
                    </div>
                    <Dropdown.Button
                      type={"primary"}
                      size={"small"}
                      onClick={() => {
                        let kedaObject: WorkloadAutoscalingKeda = {};
                        try {
                          kedaObject = jsYaml.load(keda || `{}`) as any;
                          if (typeof kedaObject !== "object" || kedaObject === null) {
                            throw new Error("KEDA configuration must be a YAML object, not a primitive value");
                          }
                        } catch (e: any) {
                          const errorMessage = e.message || "Invalid YAML syntax";
                          notification.error({
                            message: "Cannot Add Trigger",
                            description: `Current KEDA configuration has invalid YAML: ${errorMessage}`,
                          });
                          formSetValue(id, `localOptions.${index}.meta.kedaRaw`, keda);
                          return;
                        }
                        // Clear kedaRaw on successful parse
                        formSetValue(id, `localOptions.${index}.meta.kedaRaw`, "");
                        if (!kedaObject.triggers) {
                          kedaObject.triggers = [];
                        }
                        kedaObject.triggers.push({ type: "", metadata: {} });
                        const kedaString = jsYaml.dump(kedaObject);
                        formSetValue(id, `localOptions.${index}.autoscaling.keda`, kedaString);
                      }}
                      menu={{
                        style: { maxHeight: 300, overflowY: "scroll" },
                        items: kedaScalerOptions,
                        onClick: (e) => {
                          const scaler = allKedaScalers.find((s) => s.type === e.key);
                          if (!scaler) {
                            return;
                          }

                          let kedaObject: WorkloadAutoscalingKeda = {};
                          try {
                            kedaObject = jsYaml.load(keda || `{}`) as any;
                            if (typeof kedaObject !== "object" || kedaObject === null) {
                              throw new Error("KEDA configuration must be a YAML object, not a primitive value");
                            }
                          } catch (e: any) {
                            const errorMessage = e.message || "Invalid YAML syntax";
                            notification.error({
                              message: "Cannot Add Trigger",
                              description: `Current KEDA configuration has invalid YAML: ${errorMessage}`,
                            });
                            formSetValue(id, `localOptions.${index}.meta.kedaRaw`, keda);
                            return;
                          }
                          // Clear kedaRaw on successful parse
                          formSetValue(id, `localOptions.${index}.meta.kedaRaw`, "");
                          if (!kedaObject.triggers) {
                            kedaObject.triggers = [];
                          }

                          const trigger: KedaTrigger = {
                            type: scaler.type,
                            name: scaler.name,
                            metadata: scaler.metadata || {},
                          };
                          if (scaler.metricType) {
                            trigger.metricType = scaler.metricType as any;
                          }
                          kedaObject.triggers.push(trigger);
                          const kedaString = jsYaml.dump(kedaObject);
                          formSetValue(id, `localOptions.${index}.autoscaling.keda`, kedaString);
                        },
                      }}
                    >
                      Add Trigger
                    </Dropdown.Button>
                  </div>
                  <div className="flex flex-col gap-2">
                    <CpDescription>
                      <div className="flex items-center gap-1 mt-2 text-sm">
                        Learn more about KEDA{" "}
                        <ExternalLink
                          url={`${DOCS_URL}/reference/workload/autoscaling#keda`}
                          label="in our docs"
                          disableCopy
                        />
                      </div>
                    </CpDescription>
                    <CpDescription>
                      <div className="flex items-center gap-1 mb-4 text-sm">
                        See{" "}
                        <ExternalLink
                          url={`${`https://keda.sh/docs/2.17/scalers/`}`}
                          label="available keda scalers"
                          disableCopy
                        />
                      </div>
                    </CpDescription>
                  </div>
                  <CodeEditor
                    key={`local-options-${index}`}
                    value={keda}
                    setValue={(value) => formSetValue(id, `localOptions.${index}.autoscaling.keda`, value)}
                    onChange={() => {
                      const name = `localOptions.${index}.autoscaling.keda`;
                      formSet(id, `fieldStates.${name}.modified`, true);

                      const form = store.getState().form.instances[id];
                      const onChangeCounter = form.meta.onChangeCounter;
                      formSet(id, `meta.onChangeCounter`, onChangeCounter + 1);
                    }}
                    onFocus={() => {
                      const name = `localOptions.${index}.autoscaling.keda`;
                      formSet(id, `fieldStates.${name}.focused`, true);
                      formSet(id, `fieldStates.${name}.visited`, true);
                    }}
                    onBlur={() => {
                      const name = `localOptions.${index}.autoscaling.keda`;
                      formSet(id, `fieldStates.${name}.focused`, false);
                      formSet(id, `fieldStates.${name}.touched`, true);

                      // Set kedaRaw on blur if YAML is invalid
                      try {
                        const parsed = jsYaml.load(keda || "{}");
                        if (typeof parsed !== "object" || parsed === null) {
                          throw new Error("Must be an object");
                        }
                        // Clear kedaRaw if valid
                        formSetValue(id, `localOptions.${index}.meta.kedaRaw`, "");
                      } catch (e: any) {
                        formSetValue(id, `localOptions.${index}.meta.kedaRaw`, keda);
                      }

                      const form = store.getState().form.instances[id];
                      const onBlurCounter = form.meta.onBlurCounter;
                      formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
                    }}
                    language="yaml"
                    options={{ style: { fontSize: 14 } }}
                  />
                  <CpErrorWarning name={`localOptions.${index}.autoscaling.keda`} />
                </div>
              ) : null}
              {metric === "keda" ? <Divider /> : null}
              <CpFormItem>
                <div className="flex items-start gap-4">
                  <div className="flex flex-col">
                    <CpLabel name={`localOptions.${index}.autoscaling.minScale`}>
                      Min Scale
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`localOptions.${index}.autoscaling.minScale`} />
                      </Only>
                    </CpLabel>
                    <CpInputNumber name={`localOptions.${index}.autoscaling.minScale`} />
                  </div>
                  {["disabled"].includes(metric) ? null : (
                    <div className="flex flex-col">
                      <CpLabel name={`localOptions.${index}.autoscaling.maxScale`}>
                        Max Scale
                        <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                          <CpLabelDirtyIndicator name={`localOptions.${index}.autoscaling.maxScale`} />
                        </Only>
                      </CpLabel>
                      <CpInputNumber name={`localOptions.${index}.autoscaling.maxScale`} />
                    </div>
                  )}
                  <div className="flex flex-col">
                    <CpLabel name={`localOptions.${index}.autoscaling.scaleToZeroDelay`}>
                      Scale Down Delay
                      <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                        <CpLabelDirtyIndicator name={`localOptions.${index}.autoscaling.scaleToZeroDelay`} />
                      </Only>
                    </CpLabel>
                    <CpInputNumber name={`localOptions.${index}.autoscaling.scaleToZeroDelay`} addonAfter={`Seconds`} />
                  </div>
                  {type === "serverless" ? (
                    <div className="flex flex-col">
                      <CpLabel name={`localOptions.${index}.autoscaling.maxConcurrency`}>
                        Max Concurrency
                        <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
                          <CpLabelDirtyIndicator name={`localOptions.${index}.autoscaling.maxConcurrency`} />
                        </Only>
                      </CpLabel>
                      <CpInputNumber name={`localOptions.${index}.autoscaling.maxConcurrency`} />
                    </div>
                  ) : null}
                </div>
                <CpErrorWarningArray
                  names={[
                    `localOptions.${index}.autoscaling.minScale`,
                    `localOptions.${index}.autoscaling.maxScale`,
                    `localOptions.${index}.autoscaling.scaleToZeroDelay`,
                    `localOptions.${index}.autoscaling.maxConcurrency`,
                  ]}
                />
                {type === "serverless" && minScale === "0" && !scaleToZeroSuggestionApplied ? (
                  <Alert
                    type="info"
                    showIcon
                    className="mt-4 w-fit"
                    style={{ paddingTop: 16, paddingBottom: 16 }}
                    message="Scale to Zero Configuration"
                    description={
                      <div className="flex flex-col gap-2">
                        <span>
                          For scale-to-zero workloads, we recommend the following configuration to allow spin-up of a
                          replica when the first request is received:
                        </span>
                        <ul className="list-disc pl-4 my-1">
                          <li>
                            <strong>Scale Down Delay:</strong> 30 seconds
                          </li>
                          <li>
                            <strong>Timeout Seconds:</strong> 120 seconds
                          </li>
                          <li>
                            <strong>Metric:</strong> Concurrent Requests
                          </li>
                        </ul>
                        <div className="mt-1">
                          <Button type="primary" onClick={applyScaleToZeroSuggestion}>
                            Apply Suggested Configuration
                          </Button>
                        </div>
                      </div>
                    }
                  />
                ) : null}
              </CpFormItem>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
});
